import type { ReactNode } from "react"

import { PaletteScope, Section } from "@/components/marketplace"
import { SeeAllButton } from "@/components/marketplace/layout/section-header"
import { isPalette } from "@/components/marketplace/palette"
import type { FeedSection } from "@/features/feed/contract"
import { resolveSeeAllLink } from "@/features/feed/resolve-link"
import type { SectionRenderContext } from "@/features/feed/sections/types"

interface SectionFrameProps {
  section: FeedSection
  context: SectionRenderContext
  // The child renders its own header (e.g. ProductCarousel) — skip the Section wrapper.
  ownsHeader?: boolean
  // The child has no gutter of its own — wrap it in px-gut.
  gut?: boolean
  children: ReactNode
}

// Shared title.style → SectionHeader size mapping, for adapters whose component
// renders its own header and needs the same treatment SectionFrame would apply.
export const feedHeaderSize = (style?: string) => (style === "display" || style === "mega-left" ? ("display" as const) : style === "chapter" ? ("chapter" as const) : undefined)

// The reference never paints a section background. The page is one warm-grey
// field and the *objects* on it are rounded cards, so a section that wants
// emphasis becomes an inset card rather than a full-bleed tinted band.
const SURFACE = {
  soft: "bg-surface rounded-20x mx-gut overflow-hidden",
  tint: "bg-surface rounded-20x mx-gut overflow-hidden",
  grad: "bg-nelo-black-soft text-fg-inverted rounded-20x mx-gut overflow-hidden",
} as const

export const SectionFrame = ({ section, context, ownsHeader = false, gut = false, children }: SectionFrameProps) => {
  const { title, background } = section.presentation
  const palette = isPalette(section.presentation.palette) ? section.presentation.palette : undefined
  let content = gut ? <div className="px-gut">{children}</div> : children

  if (title && !ownsHeader && !context.inRow) {
    const seeAllHref = resolveSeeAllLink(title.seeAll, section.type, context.countryCode, context.page)
    const style = title.style ?? "default"
    content = (
      <Section
        title={title.text}
        accent={style === "mega-left" ? undefined : title.accent}
        eyebrow={title.eyebrow}
        action={seeAllHref ? <SeeAllButton href={seeAllHref} /> : null}
        headerSize={feedHeaderSize(style) ?? "default"}
        headerTone={style === "inverse" ? "inverse" : "default"}
        numeral={title.numeral}
        money={title.money}
      >
        {content}
      </Section>
    )
  }

  const surface = background && background !== "none" ? SURFACE[background as keyof typeof SURFACE] : undefined
  if (surface) {
    content = (
      <div className={surface}>
        <div className="w-full py-800">{content}</div>
      </div>
    )
  }

  if (palette && palette !== "brand") {
    content = <PaletteScope palette={palette}>{content}</PaletteScope>
  }

  return content
}
